<script setup>
import { ref, computed, inject, nextTick } from "vue";
import { useI18n } from "vue-i18n";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/shared/ui/sheet";
import { Empty } from "@/shared/ui/empty";
import { Skeleton } from "@/shared/ui/skeleton";
import Button from "@/shared/ui/button/Button.vue";

import ProfileSidebar from "./ProfileSidebar.vue";
import { Spinner } from "@/shared/ui/spinner";
import PersonalTab from "./tabs/PersonalTab.vue";
import DocumentsTab from "./tabs/DocumentsTab.vue";
import EducationTab from "./tabs/EducationTab.vue";
import AddressTab from "./tabs/AddressTab.vue";
import ActivityTab from "./tabs/ActivityTab.vue";
import LicenseTab from "./tabs/LicenseTab.vue";
import HistoryTab from "./tabs/HistoryTab.vue";
import ServicesTab from "./tabs/ServicesTab.vue";
import SystemTab from "./tabs/SystemTab.vue";
import UserPicker from "./UserPicker.vue";
import { USER_PROFILE_DRAWER_KEY } from "@/widgets/user-profile-drawer";

import { useNotaryDrawerData } from "../composables/useNotaryDrawerData";
import { cn } from "@/shared/lib/utils";

const { t } = useI18n();

const userProfileDrawer = inject(USER_PROFILE_DRAWER_KEY, null);

const TAB_COMPONENTS = {
  personal: PersonalTab,
  documents: DocumentsTab,
  education: EducationTab,
  address: AddressTab,
  activity: ActivityTab,
  license: LicenseTab,
  history: HistoryTab,
  services: ServicesTab,
  system: SystemTab,
};

const isOpen = ref(false);
const tab = ref("personal");
const closeCallback = ref(null);

const {
  state,
  dirty,
  load,
  selectUser,
  update,
  updateArrayItem,
  addArrayItem,
  removeArrayItem,
  toggleService,
  cancel,
  startEdit,
  save,
} = useNotaryDrawerData();

const showEmpty = computed(
  () =>
    !state.loading &&
    state.mode !== "create" &&
    (state.error || !Object.keys(state.data || {}).length)
);

const currentTab = computed(() => TAB_COMPONENTS[tab.value] || PersonalTab);

const tabBindings = computed(() => {
  // All notary tabs gate editing on `editMode && isOwn`. The drawer is
  // always operating on a selected notary (or a new one), so isOwn = editMode.
  const base = {
    data: state.data,
    editMode: state.editMode,
    isOwn: state.editMode,
  };
  if (tab.value === "personal") {
    return {
      ...base,
      organizations: state.organizations,
      roles: state.roles,
      // user fields (name, IIN, email, phone, organization) come from the
      // user entity; they're never edited through the notary drawer.
      lockUserFields: true,
    };
  }
  return base;
});

const tabListeners = computed(() => {
  switch (tab.value) {
    case "documents":
    case "history":
      return {
        "update-array-item": updateArrayItem,
        "add-array-item": addArrayItem,
        "remove-array-item": removeArrayItem,
      };
    case "services":
      return { "toggle-service": toggleService };
    default:
      return { update };
  }
});

async function open(uuid, opts = {}) {
  if (!uuid) {
    console.warn("[ProfileDrawer] open() requires a uuid");
    return;
  }
  closeCallback.value = opts.onClose || null;
  isOpen.value = true;
  tab.value = "personal";
  await load(uuid, "view");
}

async function openView(uuid, opts = {}) {
  if (!uuid) {
    console.warn("[ProfileDrawer] openView() requires a uuid");
    return;
  }
  closeCallback.value = opts.onClose || null;
  isOpen.value = true;
  tab.value = "personal";
  await load(uuid, "view");
}

async function openEdit(uuid, opts = {}) {
  if (!uuid) {
    console.warn("[ProfileDrawer] openEdit() requires a uuid");
    return;
  }
  closeCallback.value = opts.onClose || null;
  isOpen.value = true;
  tab.value = "personal";
  await load(uuid, "edit");
}

async function openCreate(opts = {}) {
  closeCallback.value = opts.onClose || null;
  isOpen.value = true;
  tab.value = "personal";
  await load(null, "create");
}

function requestClose() {
  if (state.editMode && dirty.value) {
    if (
      !window.confirm(
        t("Несохранённые изменения будут потеряны. Закрыть профиль?")
      )
    ) {
      return;
    }
  }
  doClose();
}

function doClose() {
  isOpen.value = false;
  state.editMode = false;
  state.mode = "view";
  if (closeCallback.value) {
    const cb = closeCallback.value;
    closeCallback.value = null;
    cb();
  }
}

function handleOpenChange(val) {
  if (!val) requestClose();
}

function onEditClick() {
  if (state.loading || showEmpty.value) return;
  startEdit();
}

async function onSave() {
  try {
    const resp = await save();
    if (resp?.validationFailed) return;
    if (state.mode === "create") {
      const createdUuid =
        resp?.payload?.uuid || resp?.payload?.user_id || resp?.payload?.id;
      if (createdUuid) {
        await load(createdUuid, "view");
      } else {
        doClose();
      }
    }
  } catch (e) {
    console.error(e);
  }
}

function onCancel() {
  if (state.mode === "create") {
    doClose();
    return;
  }
  cancel();
}

function onPointerDownOutside(event) {
  if (state.editMode && dirty.value) {
    event.preventDefault();
    if (
      window.confirm(
        t("Несохранённые изменения будут потеряны. Закрыть профиль?")
      )
    ) {
      doClose();
    }
  }
}

// Show user/notary switcher only when there is a matching user record to
// flip to, and we are not in create mode.
const canShowSwitcher = computed(
  () => !!userProfileDrawer && state.mode !== "create" && !!state.notaryUuid
);

function onSwitchView(view) {
  if (view !== "user" || !userProfileDrawer) return;
  if (
    state.editMode &&
    dirty.value &&
    !window.confirm(t("Несохранённые изменения будут потеряны. Сменить вид?"))
  ) {
    return;
  }
  userProfileDrawer.open(state.notaryUuid);
  nextTick(() => doClose());
}

const saveLabel = computed(() => {
  if (state.saving) return t("Сохраняем…");
  if (state.mode === "create") return t("Создать");
  return t("Сохранить");
});

const canSave = computed(() => {
  if (state.saving) return false;
  if (state.mode === "create") return !!state.selectedUser;
  if (state.mode === "edit") return dirty.value;
  return false;
});

defineExpose({
  open,
  openCreate,
  openEdit,
  openView,
  close: doClose,
});
</script>

<template>
  <Sheet :open="isOpen" @update:open="handleOpenChange">
    <SheetContent
      side="right"
      :class="
        cn(
          'p-0 flex flex-col gap-0 sm:max-w-none [&>button]:hidden border border-solid border-border shadow-sm bg-card'
        )
      "
      :style="{ width: 'min(1240px, 96vw)', maxWidth: 'min(1240px, 96vw)' }"
      @pointer-down-outside="onPointerDownOutside"
    >
      <SheetHeader class="sr-only">
        <SheetTitle>{{ $t("Профиль нотариуса") }}</SheetTitle>
      </SheetHeader>

      <div class="flex-1 flex min-h-0 overflow-hidden">
        <template v-if="state.loading">
          <aside
            class="w-[290px] shrink-0 bg-card border-r border-border pb-5 flex flex-col gap-2 overflow-hidden"
          >
            <div class="relative overflow-hidden bg-card">
              <div class="h-24 bg-muted border-0 border-b border-border" />
              <div class="px-4 pt-0 pb-2 -mt-10">
                <Skeleton
                  class="w-16 h-16 rounded-full mb-3 ring-4 ring-card"
                />
                <Skeleton class="h-4 w-40 mb-2" />
                <Skeleton class="h-3 w-20" />
              </div>
            </div>
            <div class="flex flex-col py-2">
              <div
                v-for="i in 9"
                :key="i"
                class="flex items-center gap-3.5 px-5 py-2"
              >
                <Skeleton class="h-8 flex-1" />
              </div>
            </div>
          </aside>
          <main class="flex-1 bg-muted/30 overflow-y-auto">
            <div class="max-w-[1080px] mx-auto px-6 py-10 flex flex-col gap-4">
              <Skeleton
                class="h-40 w-full"
                style="border-radius: var(--radius)"
              />
              <Skeleton
                class="h-40 w-full"
                style="border-radius: var(--radius)"
              />
            </div>
          </main>
        </template>

        <template v-else-if="showEmpty">
          <main
            class="flex-1 bg-muted/30 overflow-y-auto flex items-center justify-center"
          >
            <Empty
              :title="$t('Профиль не найден')"
              :description="$t('Не удалось загрузить данные профиля.')"
            >
              <template #footer>
                <Button @click="load(state.notaryUuid, state.mode)">{{
                  $t("Повторить")
                }}</Button>
              </template>
            </Empty>
          </main>
        </template>

        <template v-else>
          <ProfileSidebar
            :data="state.data"
            :tab="tab"
            :edit-mode="state.editMode"
            :saving="state.saving"
            :can-edit="state.mode !== 'create'"
            :show-view-switcher="canShowSwitcher"
            @set-tab="tab = $event"
            @start-edit="onEditClick"
            @cancel="onCancel"
            @switch-view="onSwitchView"
          />

          <main class="flex-1 bg-muted/30 overflow-y-auto">
            <div class="max-w-[1080px] mx-auto px-6 py-10 flex flex-col gap-6">
              <UserPicker
                v-if="state.mode === 'create'"
                :selected-user="state.selectedUser"
                @select="(u) => selectUser(u.uuid)"
                @clear="selectUser(null)"
              />

              <component
                :is="currentTab"
                v-bind="tabBindings"
                v-on="tabListeners"
              />

              <div v-if="state.editMode" class="flex justify-end gap-2 mt-6">
                <Button variant="outline" size="sm" @click="onCancel">
                  {{ state.mode === "create" ? $t("Отмена") : $t("Отменить") }}
                </Button>
                <Button size="sm" :disabled="!canSave" @click="onSave">
                  <Spinner
                    v-if="state.saving"
                    size="sm"
                    class="mr-1.5 text-current"
                  />
                  {{ saveLabel }}
                </Button>
              </div>
            </div>
          </main>
        </template>
      </div>
    </SheetContent>
  </Sheet>
</template>
